"use client";

import { Button } from "@bice_vida/ds-components-button";
import { Dialog, DialogContent, DialogClose } from "@front-account/components/common/dialog";
import { usePersistingRecoveryStore, useRecoveryStore } from "@front-account/stores/recovery";
import { env } from "@front-account/utils/libs/env";
import { ICONS } from "@login/constants/images";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import styles from "./modal-result.module.scss";

export type ResultStatusT = "HAS_MAIL" | "NO_MAIL" | "NO_PRODUCTS" | "ERROR";

type ModalResultT = {
  status: ResultStatusT;
  userEmail?: string;
};

export function ModalResult({ status, userEmail }: ModalResultT) {
  const router = useRouter();
  const t = useTranslations();
  const isOpen = useRecoveryStore((s) => s.isResultOpen);
  const setIsOpen = useRecoveryStore((s) => s.setResultOpen);
  const setIsOtpDataUpdate = usePersistingRecoveryStore((s) => s.setIsOtpDataUpdate);
  const [isLoading, setLoading] = useState(false);

  const iconMap: Record<NonNullable<ResultStatusT>, { icon: string; alt: string }> = {
    HAS_MAIL: { icon: ICONS.ENVELOPE, alt: "common.ALTS.ENVELOPE" },
    NO_MAIL: { icon: ICONS.NOTES, alt: "common.ALTS.NOTES" },
    ERROR: { icon: ICONS.CONE, alt: "common.ALTS.CONE" },
    NO_PRODUCTS: { icon: ICONS.CROSS, alt: "common.ALTS.CROSS" },
  };

  useEffect(() => {
    status === "HAS_MAIL" && router.prefetch(env.NEXT_PUBLIC_LOGIN_URL);
  }, [router, status]);

  return (
    <Dialog open={isOpen}>
      <DialogContent>
        <div className={styles.container}>
          <div className={styles.iconContainer}>
            <Image
              className={styles.icon}
              src={iconMap[status].icon}
              alt={t(iconMap[status].alt)}
              width={104}
              height={104}
              data-icon-name={iconMap[status].alt.split(".").reverse()[0].toLowerCase()}
            />
          </div>

          <h3 className={styles.title}>{t(`recovery.RESULT.${status}.TITLE`, { email: userEmail ?? "" })}</h3>

          <p className={styles.description}>
            {t.rich(`recovery.RESULT.${status}.DESCRIPTION`, {
              link: (chunks: React.ReactNode) => (
                <button
                  className={styles.link}
                  type="button"
                  onClick={() => {
                    setLoading(true);
                    setIsOtpDataUpdate(true);
                    router.push("/recuperar-cuenta/ingresar-datos");
                  }}
                >
                  {chunks}
                </button>
              ),
              bold: (chunks: React.ReactNode) => <span className={styles.bold}>{chunks}</span>,
            })}
          </p>

          <DialogClose>
            <Button
              className={styles.actionButton}
              size="m"
              loading={isLoading}
              onClick={() => {
                if (status === "NO_MAIL") {
                  setLoading(true);
                  setIsOtpDataUpdate(true);
                  router.push("/recuperar-cuenta/ingresar-datos");
                } else if (["HAS_MAIL", "NO_PRODUCTS"].includes(status)) {
                  setLoading(true);
                  router.push(env.NEXT_PUBLIC_LOGIN_URL);
                } else {
                  setIsOpen(false);
                }
              }}
            >
              {t(`recovery.RESULT.${status}.ACTION`)}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
